// i18n.service.ts
import { Injectable, effect, EffectRef, signal, untracked, computed } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

type UserDetail = { lang?: string; language?: string; locale?: string } & Record<string, unknown>;
const RTL_LANGS = new Set(['ar']);



@Injectable({ providedIn: 'root' })
export class I18nService {
	readonly lang = signal<string>('en');
	readonly ready = signal<boolean>(false);

	readonly isRtl = computed<boolean>(() => {
		const base = this.lang().split('-')[0].toLowerCase();
		return RTL_LANGS.has(base);
	});
	readonly dir = computed<'ltr'|'rtl'>(() => (this.isRtl() ? 'rtl' : 'ltr'));

	//***** LINGUE SUPPORTATE DALL'APPLICAZIONE!!!! ****
	private readonly supported = ['en', 'it', 'ar'];
	private initStarted = false;

	constructor(private translate: TranslateService) {
		this.translate.addLangs(this.supported);
		//this.translate.setDefaultLang('en');
		effect(async () => {
			const l = this.lang();
			if (this.ready()) {
				await firstValueFrom(this.translate.use(l));
			}
		});

		effect(() => {
			const l = this.lang().split('-')[0].toLowerCase();
			const dir: 'ltr' | 'rtl' = RTL_LANGS.has(l) ? 'rtl' : 'ltr';

			// protezione SSR
			if (typeof document !== 'undefined') {
				const html = document.documentElement;
				html.setAttribute('dir', dir);
				html.setAttribute('lang', this.lang());
			}
		});
		effect(() => {
			const currentDir = this.dir(); // 'ltr' | 'rtl'
			if (typeof document !== 'undefined') {
				const html = document.documentElement;
				html.setAttribute('dir', currentDir);
				html.setAttribute('lang', this.lang());
			}
		});
	}

	/** Chiama sempre questa: garantisce init one-shot */
	async ensureInitialized(): Promise<void> {
		if (this.ready()) return;
		if (this.initStarted) {
			await this.waitUntilReady();
			return;
		}
		this.initStarted = true;
		await this.init();
	}

	private async init(): Promise<void> {
		const decided = this.decideInitialLang();
		this.lang.set(decided);
		await firstValueFrom(this.translate.use(decided));
		this.ready.set(true);
	}

	private decideInitialLang(): string {
		try {
			const raw = localStorage.getItem('aion_user_detail');
			if (raw) {
				const parsed = JSON.parse(raw) as UserDetail;
				const fromUser =
					(parsed.lang ?? parsed.language ?? parsed.locale ?? '').toString().toLowerCase();
				const norm = this.normalizeLang(fromUser);
				if (norm) return norm;
			}
		} catch { }

		const browser = (this.translate.getBrowserLang() ?? 'en').toLowerCase();
		const norm = this.normalizeLang(browser);
		return norm ?? 'en';
	}

	private normalizeLang(l: string | null | undefined): string | null {
		if (!l) return null;
		const base = l.split('-')[0];
		if (this.supported.includes(l)) return l;
		if (this.supported.includes(base)) return base;
		return null;
	}

	async setLang(next: string) {
		const norm = this.normalizeLang(next) ?? this.lang();
		this.lang.set(norm);
		await firstValueFrom(this.translate.use(norm));
		try {
			const raw = localStorage.getItem('aion_user_detail');
			if (raw) {
				const parsed = JSON.parse(raw) as UserDetail;
				parsed.lang = norm;
				localStorage.setItem('aion_user_detail', JSON.stringify(parsed));
			}
		} catch { }
	}

	/** Promise che risolve quando ready = true */
	waitUntilReady(): Promise<void> {
		if (this.ready()) return Promise.resolve();
		return new Promise<void>((resolve) => {
			let ref: EffectRef;
			ref = effect(() => {
				const isReady = untracked(() => this.ready());
				if (isReady) {
					ref.destroy();
					resolve();
				}
			});
		});
	}
}
